/**
 * Product frames for the marketing site.
 *
 * Two kinds, labelled as such wherever they render:
 *   - Captures (`dashboard`, `estimates`, `marketing`): production PNGs of the
 *     shipped application, taken through the disposable "Summit Ridge
 *     Roofing" workspace via scripts/capture-marketing-assets/.
 *   - Illustrations (`illustration: true`): choreographed films drawn in code
 *     (films/*.html, rendered by render-film.mjs / render-hero-film.mjs). Their
 *     maps, addresses, and figures are illustrative, not captured product
 *     output, and every surface that shows one says so.
 *
 * Hygiene rules:
 *   - never point these slots at /screenshots/* (Dev chrome leaks), and
 *   - every referenced file must exist in public/product-frames/ — a
 *     `video` entry without an asset fires a 404 on every visit.
 */

/**
 * Where the recording's AI-detection act starts; the lead-in is the address
 * form. Exported so every player of widget-tour.webm seeks and gates on the
 * same thresholds.
 */
export const DETECTION_START = 29;
/** Playback at or past this point is inside the detection act, safe to reveal.
 *  Below the requested start: a seek can settle on an earlier keyframe, so
 *  readiness must tolerate a position slightly below DETECTION_START. */
export const READY_AT = 28;

export type ProductFrameId =
  | 'dashboard'
  | 'instant'
  | 'ai'
  | 'pipeline'
  | 'estimates'
  | 'invoices'
  | 'canvassing'
  | 'marketing';

export interface ProductFrame {
  id: ProductFrameId;
  label: string;
  description: string;
  still: string;
  /** VP9 WebM, the first source players offer. */
  video?: string;
  /** H.264 MP4 fallback for browsers that cannot decode the WebM. */
  videoMp4?: string;
  alt: string;
  /** Seek target for films that have no lead-in (defaults to the widget
   *  recording's 29s detection act). */
  filmStart?: number;
  /** Playback at/after this time reveals the film (defaults to 28). */
  filmReady?: number;
  /** A code-drawn film rather than a capture of the shipped app. */
  illustration?: boolean;
}

export const PRODUCT_FRAMES: Record<ProductFrameId, ProductFrame> = {
  dashboard: {
    id: 'dashboard',
    label: 'Command center',
    description:
      'Jobs, pipeline value, and crew load in one office view — captured live from the shipped dashboard.',
    still: '/product-frames/dashboard.webp',
    alt: 'Rooflyzer OS dashboard showing needs-attention triage, today’s focus, and collection stats',
  },
  instant: {
    id: 'instant',
    label: 'Instant Roof Quote',
    description:
      'Illustrated: the embeddable quote widget outlines the roof and prices good, better, and best options while the homeowner watches.',
    still: '/product-frames/videos/hero-instant-quote-poster.jpg',
    video: '/product-frames/videos/hero-instant-quote.webm',
    videoMp4: '/product-frames/videos/hero-instant-quote.mp4',
    // The film is choreographed: it opens on the detection act and needs no seek.
    filmStart: 0,
    filmReady: 1,
    illustration: true,
    alt: 'Illustration: a roof outline on a dark map beside good, better, and best prices for 1420 Yellowstone Rd',
  },
  ai: {
    id: 'ai',
    label: 'AI front office',
    description:
      'Illustrated: voice agents that answer calls, quote roofs, and book estimates — pre-trained for the roofing trade.',
    still: '/product-frames/videos/voice-poster.jpg',
    video: '/product-frames/videos/voice.webm',
    videoMp4: '/product-frames/videos/voice.mp4',
    filmStart: 0,
    filmReady: 1,
    illustration: true,
    alt: 'Illustration: an AI receptionist call transcript offering a homeowner a free hail inspection on Tuesday at 10 AM',
  },
  pipeline: {
    id: 'pipeline',
    label: 'Sales pipeline',
    description: 'Illustrated: every lead from first knock to paid invoice without a second tool.',
    still: '/product-frames/videos/pipeline-poster.jpg',
    video: '/product-frames/videos/pipeline.webm',
    videoMp4: '/product-frames/videos/pipeline.mp4',
    filmStart: 0,
    filmReady: 1,
    illustration: true,
    alt: 'Illustration: a four-stage sales pipeline board with the 1420 Yellowstone Rd deal in Won and $21,100 closed this month',
  },
  estimates: {
    id: 'estimates',
    label: 'Estimates',
    description: 'Good / better / best pricing on the same takeoff.',
    still: '/product-frames/estimates.webp',
    alt: 'Rooflyzer estimates workspace',
  },
  invoices: {
    id: 'invoices',
    label: 'Invoices',
    description: 'Illustrated: branded invoices and payment status on every job.',
    still: '/product-frames/videos/invoicing-poster.jpg',
    video: '/product-frames/videos/invoicing.webm',
    videoMp4: '/product-frames/videos/invoicing.mp4',
    filmStart: 0,
    filmReady: 1,
    illustration: true,
    alt: 'Illustration: an invoice stamped paid with a $0 balance beside a collected-this-month card',
  },
  canvassing: {
    id: 'canvassing',
    label: 'Canvassing',
    description: 'Illustrated: hail windows, routes, and knock lists for the field.',
    still: '/product-frames/videos/canvassing-poster.jpg',
    video: '/product-frames/videos/canvassing.webm',
    videoMp4: '/product-frames/videos/canvassing.mp4',
    filmStart: 0,
    filmReady: 1,
    illustration: true,
    alt: 'Illustration: two canvassing territories on a dark map, one with knock pins, beside 47 knocked of 312 addresses',
  },
  marketing: {
    id: 'marketing',
    label: 'Marketing',
    description: 'Nurture journeys for storm and retail without a second CRM.',
    still: '/product-frames/marketing.webp',
    alt: 'Rooflyzer marketing automation overview',
  },
};

export interface FilmSource {
  src: string;
  type: string;
}

/**
 * A film's encodes in the order a player offers them: the VP9 WebM, then the
 * H.264 MP4 for browsers that cannot decode it (design/ART_DIRECTION.md).
 * Empty for a frame with no film.
 */
export function filmSources(frame: ProductFrame): FilmSource[] {
  const sources: FilmSource[] = [];
  if (frame.video) sources.push({ src: frame.video, type: 'video/webm; codecs="vp9"' });
  if (frame.videoMp4) sources.push({ src: frame.videoMp4, type: 'video/mp4' });
  return sources;
}

export const PRODUCT_TOUR_ORDER: ProductFrameId[] = [
  'instant',
  'ai',
  'dashboard',
  'pipeline',
  'estimates',
];
